import { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/AdminLayout";
import "./Settings.css";

const API_BASE = process.env.REACT_APP_API_URL;
const ORG_ID   = "resh-dev";

function authHeaders(contentType = "application/json") {
  return {
    "Content-Type": contentType,
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    OrgId: ORG_ID,
  };
}

// ─── Snackbar ─────────────────────────────────────────────────────────────────
function useSnackbar() {
  const [snack, setSnack] = useState(null);
  const timerRef = useRef();
  const showSnack = useCallback((msg, type = "error") => {
    clearTimeout(timerRef.current);
    setSnack({ msg, type, id: Date.now() });
    timerRef.current = setTimeout(() => setSnack(null), 4500);
  }, []);
  const dismissSnack = useCallback(() => { clearTimeout(timerRef.current); setSnack(null); }, []);
  return { snack, showSnack, dismissSnack };
}

function Snackbar({ snack, onDismiss }) {
  if (!snack) return null;
  return (
    <div key={snack.id} className={`snackbar snackbar--${snack.type}`} role="alert" aria-live="assertive">
      <span className="snackbar__icon">
        {snack.type === "error" ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span className="snackbar__msg">{snack.msg}</span>
      <button className="snackbar__close" onClick={onDismiss} aria-label="Dismiss">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13">
          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}

// ─── Searchable Dropdown (same UI as Gallery's CategoryDropdown) ──────────────
function SearchDropdown({ options, value, onChange, placeholder = "Select…", includeAll = false }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const base     = includeAll ? ["All", ...options] : options;
  const filtered = base.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  const isEmpty  = !value || (includeAll && value === "All");

  const select = (opt) => { onChange(opt); setOpen(false); setSearch(""); };

  return (
    <div className="cat-dropdown" ref={ref}>
      <button
        type="button"
        className={`cat-dropdown__trigger${open ? " cat-dropdown__trigger--open" : ""}${isEmpty ? " cat-dropdown__trigger--placeholder" : ""}`}
        onClick={() => setOpen((p) => !p)}
      >
        <span className="cat-dropdown__trigger-label">{value || placeholder}</span>
        <svg className="cat-dropdown__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="cat-dropdown__panel">
          <div className="cat-dropdown__search-wrap">
            <svg className="cat-dropdown__search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input className="cat-dropdown__search" type="text" placeholder="Search…"
              value={search} onChange={(e) => setSearch(e.target.value)} autoFocus />
          </div>
          <ul className="cat-dropdown__list">
            {filtered.length === 0 ? (
              <li className="cat-dropdown__empty">No results</li>
            ) : filtered.map((opt) => (
              <li key={opt}
                className={`cat-dropdown__option${value === opt ? " cat-dropdown__option--active" : ""}`}
                onClick={() => select(opt)}>
                <span>{opt}</span>
                {value === opt && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
function Modal({ id, title, onClose, children, actions, size = "md" }) {
  return (
    <div className="settings-modal__overlay" role="dialog" aria-modal="true"
      aria-labelledby={id} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`settings-modal settings-modal--${size}`}>
        <div className="settings-modal__header">
          <h3 className="settings-modal__title" id={id}>{title}</h3>
          <button type="button" className="settings-modal__close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div className="settings-modal__body">{children}</div>
        {actions && <div className="settings-modal__actions">{actions}</div>}
      </div>
    </div>
  );
}

// ─── Confirm delete modal ─────────────────────────────────────────────────────
function ConfirmDeleteModal({ label, onClose, onConfirm, loading }) {
  return (
    <Modal id="del-modal" title="Confirm Delete" onClose={onClose} size="sm"
      actions={<>
        <button className="settings-btn settings-btn--ghost" onClick={onClose} disabled={loading}>Cancel</button>
        <button className="settings-btn settings-btn--danger" onClick={onConfirm} disabled={loading}>
          {loading ? "Deleting…" : "Delete"}
        </button>
      </>}>
      <p className="settings-modal__confirm-msg">Delete <strong>"{label}"</strong>?</p>
      <p className="settings-modal__confirm-warn">This cannot be undone.</p>
    </Modal>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// FUNCTION TYPES
// ══════════════════════════════════════════════════════════════════════════════

function FunctionTypeModal({ mode, item, onClose, onSaved, showSnack }) {
  const [name, setName] = useState(item?.name || "");
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { showSnack("Name is required."); return; }
    setSaving(true);
    try {
      const url  = isEdit ? `${API_BASE}/entities/function_types/${item._id}` : `${API_BASE}/entities/function_types`;
      const body = isEdit ? { name: name.trim() } : { name: name.trim(), status: "Active" };
      const res  = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: authHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || data.message || "Something went wrong.");
      showSnack(isEdit ? "Category updated." : "Category added.", "success");
      onSaved(); onClose();
    } catch (err) { showSnack(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal id="func-modal" title={isEdit ? "Edit Category" : "Add Category"} onClose={onClose}
      actions={<>
        <button className="settings-btn settings-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="settings-btn settings-btn--primary" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Category"}
        </button>
      </>}>
      <form onSubmit={handleSubmit}>
        <div className="settings-modal__field">
          <label className="settings-modal__label" htmlFor="func-name">
            Name <span className="settings-modal__required">*</span>
          </label>
          <input id="func-name" className="settings-modal__input" type="text"
            placeholder="e.g. Wedding, Portrait…" value={name}
            onChange={(e) => setName(e.target.value)} autoFocus required />
        </div>
      </form>
    </Modal>
  );
}

function FunctionTypeRow({ item, onEdit, onDelete }) {
  return (
    <div className="func-row">
      <div className="func-row__info">
        <span className="func-row__name">{item.name}</span>
      </div>
      <div className="func-row__actions">
        <button type="button" className="func-row__icon-btn" onClick={() => onEdit(item)} title="Edit" aria-label={`Edit ${item.name}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
        {/* <button type="button" className="func-row__icon-btn func-row__icon-btn--danger" onClick={() => onDelete(item)} title="Delete" aria-label={`Delete ${item.name}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button> */}
      </div>
    </div>
  );
}

// ── Function Types section panel ──────────────────────────────────────────────
function FunctionTypesPanel({ showSnack }) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null); // null | {type,item?}

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/entities/filter/function_types`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ sort: [{ colId: "name", sort: "asc" }] }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to load.");
      setItems(data?.data?.[0]?.response ?? []);
    } catch (err) { showSnack(err.message); }
    finally { setLoading(false); }
  }, [showSnack]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async () => {
    try {
      const res  = await fetch(`${API_BASE}/entities/function_types/${modal.item._id}`, { method: "DELETE", headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error_msg || "Delete failed.");
      showSnack("Category deleted.", "success");
      fetchItems(); setModal(null);
    } catch (err) { showSnack(err.message); }
  };

  return (
    <>
      <div className="settings-content__head">
        <div>
          <h2 className="settings-content__title">Function Types</h2>
          <p className="settings-content__subtitle">Photography categories shown in the gallery and upload form.</p>
        </div>
        <button className="settings-btn settings-btn--primary" onClick={() => setModal({ type: "add" })}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add Category
        </button>
      </div>

      <div className="func-table" role="list" style={{ position: "relative", minHeight: "200px" }}>
        {loading ? (
          <div className="app-loading-overlay">
            <div className="app-spinner"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="func-table__empty">No categories yet — add one above.</div>
        ) : (
          items.map((item) => (
            <FunctionTypeRow key={item._id} item={item}
              onEdit={(i) => setModal({ type: "edit", item: i })}
              onDelete={(i) => setModal({ type: "delete", item: i })}
            />
          ))
        )}
      </div>

      {modal?.type === "add"    && <FunctionTypeModal mode="add"  onClose={() => setModal(null)} onSaved={fetchItems} showSnack={showSnack} />}
      {modal?.type === "edit"   && <FunctionTypeModal mode="edit" item={modal.item} onClose={() => setModal(null)} onSaved={fetchItems} showSnack={showSnack} />}
      {modal?.type === "delete" && (
        <ConfirmDeleteModal label={modal.item.name} onClose={() => setModal(null)} onConfirm={handleDelete} />
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PLANS
// ══════════════════════════════════════════════════════════════════════════════

// Parse features — stored as comma string or array
function parseFeatures(raw) {
  if (Array.isArray(raw)) return raw.join(", ");
  return raw || "";
}

function PlanModal({ mode, plan, functionTypes, onClose, onSaved, showSnack }) {
  const isEdit = mode === "edit";
  const [name,         setName]         = useState(plan?.name         || "");
  const [price,        setPrice]        = useState(plan?.price != null ? String(plan.price) : "");

  // raw digits only — used for DB; displayed with Indian comma formatting
  const displayPrice = price === "" ? "" : Number(price.replace(/,/g, "")).toLocaleString("en-IN");

  const handlePriceChange = (e) => {
    // strip everything except digits
    const digits = e.target.value.replace(/[^0-9]/g, "");
    setPrice(digits);
  };
  const [description,  setDescription]  = useState(plan?.description  || "");
  const [features,     setFeatures]     = useState(parseFeatures(plan?.features));
  const [functionType,   setFunctionType]   = useState(plan?.function_type    || "");
  const [functionTypeId, setFunctionTypeId] = useState(plan?.function_type_id || "");
  const [isPopular,    setIsPopular]    = useState(plan?.is_popular   || false);
  const [saving,       setSaving]       = useState(false);

  const parsedFeatures = features.split(",").map((f) => f.trim()).filter(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim())        { showSnack("Plan name is required.");       return; }
    if (price === "")        { showSnack("Price is required.");           return; }
    if (!functionType)       { showSnack("Please select a function type."); return; }

    setSaving(true);
    try {
      const url  = isEdit ? `${API_BASE}/entities/plans/${plan._id}` : `${API_BASE}/entities/plans`;
      const body = {
        name:          name.trim(),
        price:         Number(price),
        currency:      "INR",
        features:      parsedFeatures,
        function_type:    functionType,
        function_type_id: functionTypeId,
        is_popular:    isPopular,
        ...(isEdit ? {} : { status: "Active" }),
      };
      const res  = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: authHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || data.message || "Something went wrong.");
      showSnack(isEdit ? "Plan updated." : "Plan created.", "success");
      onSaved(); onClose();
    } catch (err) { showSnack(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal id="plan-modal" title={isEdit ? "Edit Plan" : "Create Plan"} onClose={onClose} size="lg"
      actions={<>
        <button className="settings-btn settings-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="settings-btn settings-btn--primary" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Plan"}
        </button>
      </>}>
      <form onSubmit={handleSubmit} className="plan-form">

        {/* Row 1: Name + Function Type */}
        <div className="plan-form__row">
          <div className="settings-modal__field">
            <label className="settings-modal__label">Plan Name <span className="settings-modal__required">*</span></label>
            <input className="settings-modal__input" type="text" placeholder="e.g. Basic, Premium…"
              value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div className="settings-modal__field">
            <label className="settings-modal__label">Function Type <span className="settings-modal__required">*</span></label>
            <SearchDropdown
              options={functionTypes.map((ft) => ft.name)}
              value={functionType}
              onChange={(name) => {
                setFunctionType(name);
                const found = functionTypes.find((ft) => ft.name === name);
                setFunctionTypeId(found ? found._id : "");
              }}
              placeholder="Select a type…"
            />
          </div>
        </div>

        {/* Row 2: Price */}
        <div className="settings-modal__field">
          <label className="settings-modal__label">Price <span className="settings-modal__required">*</span></label>
          <input className="settings-modal__input" type="text" inputMode="numeric" placeholder="e.g. 25,000"
            value={displayPrice} onChange={handlePriceChange} />
        </div>

        {/* Description */}
        <div className="settings-modal__field">
          <label className="settings-modal__label">Description</label>
          <textarea className="settings-modal__textarea" rows={2}
            placeholder="Short description of this plan…"
            value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        {/* Features */}
        <div className="settings-modal__field">
          <label className="settings-modal__label">
            Features
            <span className="settings-modal__hint"> — comma separated</span>
          </label>
          <textarea className="settings-modal__textarea" rows={3}
            placeholder="e.g. Up to 200 photos, Edited album, Online delivery…"
            value={features} onChange={(e) => setFeatures(e.target.value)} />
          {parsedFeatures.length > 0 && (
            <div className="plan-form__feature-preview">
              {parsedFeatures.map((f, i) => (
                <span key={i} className="plan-form__feature-chip">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="10" height="10">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  {f}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Popular toggle */}
        <label className="plan-form__popular-toggle">
          <input type="checkbox" checked={isPopular} onChange={(e) => setIsPopular(e.target.checked)} />
          <span className="plan-form__popular-label">Mark as Popular</span>
        </label>

      </form>
    </Modal>
  );
}

// ── Plan card (displayed in the grid) ─────────────────────────────────────────
function PlanCard({ plan, onEdit, onDelete }) {
  const features = Array.isArray(plan.features) ? plan.features : [];
  const priceLabel = `₹${Number(plan.price).toLocaleString("en-IN")}`;

  return (
    <div className={`plan-card${plan.is_popular ? " plan-card--popular" : ""}`}>
      {plan.is_popular && <span className="plan-card__popular-badge">Popular</span>}

      <div className="plan-card__head">
        <span className="plan-card__name">{plan.name}</span>
        <span className="plan-card__type">{plan.function_type}</span>
        {plan.description && (
          <span className="plan-card__desc">{plan.description}</span>
        )}
      </div>

      <div className="plan-card__price">
        <span className="plan-card__amount">{priceLabel}</span>
      </div>

      {features.length > 0 && (
        <ul className="plan-card__features">
          {features.map((f, i) => (
            <li key={i} className="plan-card__feature">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12" className="plan-card__check">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {f}
            </li>
          ))}
        </ul>
      )}

      <div className="plan-card__actions">
        <button className="plan-card__action-btn" onClick={() => onEdit(plan)} title="Edit plan">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="14" height="14">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          Edit
        </button>
        <button className="plan-card__action-btn plan-card__action-btn--danger" onClick={() => onDelete(plan)} title="Delete plan">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="14" height="14">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
          Delete
        </button>
      </div>
    </div>
  );
}

// ── Plan row (displayed in the grid/table view) ─────────────────────────────
function PlanRow({ plan, onEdit, onDelete }) {
  const features = Array.isArray(plan.features) ? plan.features : [];
  const priceLabel = `₹${Number(plan.price).toLocaleString("en-IN")}`;

  return (
    <div className={`plan-row${plan.is_popular ? " plan-row--popular" : ""}`}>
      <div className="plan-row__cell plan-row__cell--name">
        <div className="plan-row__name-wrap">
          <span className="plan-row__name">{plan.name}</span>
          {plan.is_popular && <span className="plan-card__popular-badge plan-row__badge">Popular</span>}
        </div>
        {plan.description && <span className="plan-row__desc">{plan.description}</span>}
      </div>

      <div className="plan-row__cell plan-row__cell--type">
        <span className="plan-row__type">{plan.function_type || "General"}</span>
      </div>

      <div className="plan-row__cell plan-row__cell--price">
        <span className="plan-row__price">{priceLabel}</span>
      </div>

      <div className="plan-row__cell plan-row__cell--features">
        {features.length > 0 ? (
          <div className="plan-row__features-chips">
            {features.map((f, i) => (
              <span key={i} className="plan-row__feature-chip">{f}</span>
            ))}
          </div>
        ) : (
          <span className="plan-row__empty-text">—</span>
        )}
      </div>

      <div className="plan-row__cell plan-row__cell--actions">
        <button className="func-row__icon-btn" onClick={() => onEdit(plan)} title="Edit plan" aria-label={`Edit ${plan.name}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        </button>
        <button className="func-row__icon-btn func-row__icon-btn--danger" onClick={() => onDelete(plan)} title="Delete plan" aria-label={`Delete ${plan.name}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ── Plans section panel ───────────────────────────────────────────────────────
function PlansPanel({ showSnack }) {
  const [plans,         setPlans]         = useState([]);
  const [functionTypes, setFunctionTypes] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [filterType,    setFilterType]    = useState("All");
  const [viewMode,      setViewMode]      = useState(() => localStorage.getItem("admin_plans_view_mode") || "card");
  const [modal,         setModal]         = useState(null);

  const changeViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem("admin_plans_view_mode", mode);
  };

  const fetchFunctionTypes = useCallback(async () => {
    try {
      const res  = await fetch(`${API_BASE}/entities/filter/function_types`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ sort: [{ colId: "name", sort: "asc" }] }),
      });
      const data = await res.json();
      if (res.ok && data.status === 200) setFunctionTypes(data?.data?.[0]?.response ?? []);
    } catch (_) {}
  }, []);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/entities/filter/plans`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ sort: [{ colId: "function_type", sort: "asc" }] }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to load plans.");
      setPlans(data?.data?.[0]?.response ?? []);
    } catch (err) { showSnack(err.message); }
    finally { setLoading(false); }
  }, [showSnack]);

  useEffect(() => { fetchFunctionTypes(); fetchPlans(); }, [fetchFunctionTypes, fetchPlans]);

  const handleDelete = async () => {
    try {
      const res = await fetch(`${API_BASE}/entities/plans/${modal.plan._id}`, { method: "DELETE", headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error_msg || "Delete failed.");
      showSnack("Plan deleted.", "success");
      fetchPlans(); setModal(null);
    } catch (err) { showSnack(err.message); }
  };

  // Group plans by function_type
  const typeNames = ["All", ...functionTypes.map((ft) => ft.name)];
  const visiblePlans = filterType === "All" ? plans : plans.filter((p) => p.function_type === filterType);

  // Group visible plans by function_type for display
  const grouped = visiblePlans.reduce((acc, p) => {
    const key = p.function_type || "Uncategorised";
    if (!acc[key]) acc[key] = [];
    acc[key].push(p);
    return acc;
  }, {});

  return (
    <>
      {/* Header */}
      <div className="settings-content__head">
        <div>
          <h2 className="settings-content__title">Plans</h2>
          <p className="settings-content__subtitle">Pricing plans grouped by photography category.</p>
        </div>
        <button className="settings-btn settings-btn--primary" onClick={() => setModal({ type: "add" })}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Create Plan
        </button>
      </div>

      {/* Toolbar (Filter & View mode toggle) */}
      <div className="plans-toolbar">
        <div className="plans-filter">
          <span className="plans-filter__label">Function Type</span>
          <SearchDropdown
            options={functionTypes.map((ft) => ft.name)}
            value={filterType}
            onChange={setFilterType}
            placeholder="All"
            includeAll
          />
        </div>

        <div className="plans-view-toggle">
          <button
            type="button"
            className={`plans-view-btn${viewMode === "card" ? " active" : ""}`}
            onClick={() => changeViewMode("card")}
            title="Card View"
            aria-label="Switch to Card View"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
            </svg>
            <span>Card</span>
          </button>
          <button
            type="button"
            className={`plans-view-btn${viewMode === "grid" ? " active" : ""}`}
            onClick={() => changeViewMode("grid")}
            title="Grid / Table View"
            aria-label="Switch to Grid View"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
            <span>Grid</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading ? (
          <div className="app-loading-overlay">
            <div className="app-spinner"></div>
          </div>
        ) : visiblePlans.length === 0 ? (
          <div className="func-table__empty">No plans yet — create one above.</div>
        ) : (
          Object.entries(grouped).map(([typeName, typePlans]) => (
            <div key={typeName} className="plans-group">
              <h3 className="plans-group__title">{typeName}</h3>
              {viewMode === "card" ? (
                <div className="plans-grid">
                  {typePlans.map((plan) => (
                    <PlanCard
                      key={plan._id}
                      plan={plan}
                      onEdit={(p) => setModal({ type: "edit", plan: p })}
                      onDelete={(p) => setModal({ type: "delete", plan: p })}
                    />
                  ))}
                </div>
              ) : (
                <div className="plans-table-wrap">
                  <div className="plans-table-head">
                    <span className="plans-table-head__col col-name">Plan Name</span>
                    <span className="plans-table-head__col col-type">Category</span>
                    <span className="plans-table-head__col col-price">Price</span>
                    <span className="plans-table-head__col col-features">Features</span>
                    <span className="plans-table-head__col col-actions">Actions</span>
                  </div>
                  <div className="plans-table-body">
                    {typePlans.map((plan) => (
                      <PlanRow
                        key={plan._id}
                        plan={plan}
                        onEdit={(p) => setModal({ type: "edit", plan: p })}
                        onDelete={(p) => setModal({ type: "delete", plan: p })}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modals */}
      {modal?.type === "add" && (
        <PlanModal mode="add" functionTypes={functionTypes} onClose={() => setModal(null)} onSaved={fetchPlans} showSnack={showSnack} />
      )}
      {modal?.type === "edit" && (
        <PlanModal mode="edit" plan={modal.plan} functionTypes={functionTypes} onClose={() => setModal(null)} onSaved={fetchPlans} showSnack={showSnack} />
      )}
      {modal?.type === "delete" && (
        <ConfirmDeleteModal label={modal.plan.name} onClose={() => setModal(null)} onConfirm={handleDelete} />
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS NAV
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// SYSTEM USERS
// ══════════════════════════════════════════════════════════════════════════════

const USER_ROLES = ["Admin", "Editor", "Viewer"];

function UserModal({ mode, user, onClose, onSaved, showSnack }) {
  const isEdit = mode === "edit";
  const [name,     setName]     = useState(user?.name  || "");
  const [email,    setEmail]    = useState(user?.email || user?.email_id || "");
  const [role,     setRole]     = useState(user?.role  || "Editor");
  const [password, setPassword] = useState("");
  const [showPw,   setShowPw]   = useState(false);
  const [saving,   setSaving]   = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim())  { showSnack("Name is required.");  return; }
    if (!email.trim()) { showSnack("Email is required."); return; }
    if (!isEdit && !password.trim()) { showSnack("Password is required for new users."); return; }

    setSaving(true);
    try {
      const token = localStorage.getItem("admin_token");
      const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}`, OrgId: ORG_ID };

      if (isEdit) {
        const body = { name: name.trim(), role };
        if (password.trim()) body.password = password.trim();
        const res  = await fetch(`${API_BASE}/entities/users/${user._id}`, { method: "PUT", headers, body: JSON.stringify(body) });
        const data = await res.json();
        if (!res.ok || data.status !== 200) throw new Error(data.error_msg || data.message || "Update failed.");
        showSnack("User updated.", "success");
      } else {
        const res  = await fetch(`${API_BASE}/auth/create-user`, {
          method: "POST", headers,
          body: JSON.stringify({ name: name.trim(), email: email.trim(), password: password.trim(), role }),
        });
        const data = await res.json();
        if (!res.ok || data.status !== 200) throw new Error(data.error_msg || data.message || "Create failed.");
        showSnack("User created.", "success");
      }
      onSaved(); onClose();
    } catch (err) { showSnack(err.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal id="user-modal" title={isEdit ? "Edit User" : "Add User"} onClose={onClose} size="md"
      actions={<>
        <button className="settings-btn settings-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="settings-btn settings-btn--primary" onClick={handleSubmit} disabled={saving}>
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Add User"}
        </button>
      </>}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

        {/* Name */}
        <div className="settings-modal__field">
          <label className="settings-modal__label">Name <span className="settings-modal__required">*</span></label>
          <input className="settings-modal__input" type="text" placeholder="Full name"
            value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>

        {/* Email — readonly on edit */}
        <div className="settings-modal__field">
          <label className="settings-modal__label">Email <span className="settings-modal__required">{!isEdit ? "*" : ""}</span></label>
          <input className="settings-modal__input" type="email" placeholder="user@example.com"
            value={email} onChange={(e) => setEmail(e.target.value)} readOnly={isEdit}
            style={isEdit ? { opacity: 0.55, cursor: "not-allowed" } : {}} />
        </div>

        {/* Role */}
        <div className="settings-modal__field">
          <label className="settings-modal__label">Role <span className="settings-modal__required">*</span></label>
          <SearchDropdown options={USER_ROLES} value={role} onChange={setRole} placeholder="Select role…" />
        </div>

        {/* Password */}
        <div className="settings-modal__field">
          <label className="settings-modal__label">
            {isEdit ? "New Password" : "Password *"}
            {isEdit && <span className="settings-modal__hint" style={{ marginLeft: "0.4rem" }}>— leave blank to keep current</span>}
          </label>
          <div className="settings-modal__pw-wrap">
            <input
              className="settings-modal__input settings-modal__input--pw"
              type={showPw ? "text" : "password"}
              placeholder={isEdit ? "Leave blank to keep current" : "Password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="settings-modal__pw-toggle"
              onClick={() => setShowPw((p) => !p)}
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

      </form>
    </Modal>
  );
}

function UserRow({ user, onEdit, onDelete }) {
  const initial       = (user.name || user.email || "?")[0].toUpperCase();
  const isSuperAdmin  = (user.role || "").toLowerCase() === "super-admin";

  return (
    <div className="func-row">
      <div className="user-row__avatar">{initial}</div>

      <div className="func-row__info">
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span className="func-row__name">{user.name || "—"}</span>
          {isSuperAdmin && (
            <span className="user-row__super-badge">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              Super Admin
            </span>
          )}
        </div>
        <span style={{ fontFamily: "var(--font-body)", fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
          {user.email || user.email_id || ""}
        </span>
      </div>

      {!isSuperAdmin && (
        <span className="func-row__badge func-row__badge--active" style={{ textTransform: "none" }}>
          {user.role || "—"}
        </span>
      )}

      <div className="func-row__actions">
        {isSuperAdmin ? (
          <span className="user-row__protected" title="Super Admin cannot be modified">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="14" height="14">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Protected
          </span>
        ) : (
          <>
            <button type="button" className="func-row__icon-btn" onClick={() => onEdit(user)} title="Edit">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button type="button" className="func-row__icon-btn func-row__icon-btn--danger" onClick={() => onDelete(user)} title="Delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="15" height="15">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SystemUsersPanel({ showSnack }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("admin_token");
      const res   = await fetch(`${API_BASE}/entities/filter/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, OrgId: ORG_ID },
        body: JSON.stringify({ sort: [{ colId: "name", sort: "asc" }] }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 200) throw new Error(data.error_msg || "Failed to load users.");
      setUsers(data?.data?.[0]?.response ?? []);
    } catch (err) { showSnack(err.message); }
    finally { setLoading(false); }
  }, [showSnack]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res   = await fetch(`${API_BASE}/entities/users/${modal.user._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, OrgId: ORG_ID },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error_msg || "Delete failed.");
      showSnack("User deleted.", "success");
      fetchUsers(); setModal(null);
    } catch (err) { showSnack(err.message); }
  };

  return (
    <>
      <div className="settings-content__head">
        <div>
          <h2 className="settings-content__title">System Users</h2>
          <p className="settings-content__subtitle">Manage admin panel users and their access roles.</p>
        </div>
        <button className="settings-btn settings-btn--primary" onClick={() => setModal({ type: "add" })}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" width="13" height="13">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Add User
        </button>
      </div>

      <div className="func-table" role="list" style={{ position: "relative", minHeight: "200px" }}>
        {loading ? (
          <div className="app-loading-overlay">
            <div className="app-spinner"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="func-table__empty">No users found.</div>
        ) : (
          users.map((u) => (
            <UserRow key={u._id} user={u}
              onEdit={(user) => setModal({ type: "edit", user })}
              onDelete={(user) => setModal({ type: "delete", user })}
            />
          ))
        )}
      </div>

      {modal?.type === "add"    && <UserModal mode="add"  onClose={() => setModal(null)} onSaved={fetchUsers} showSnack={showSnack} />}
      {modal?.type === "edit"   && <UserModal mode="edit" user={modal.user} onClose={() => setModal(null)} onSaved={fetchUsers} showSnack={showSnack} />}
      {modal?.type === "delete" && (
        <ConfirmDeleteModal label={modal.user.name || modal.user.email} onClose={() => setModal(null)} onConfirm={handleDelete} />
      )}
    </>
  );
}

const SETTINGS_NAV = [
  {
    key: "function_types",
    label: "Function Types",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="15" height="15">
        <path d="M4 6h16M4 10h16M4 14h10M4 18h6" />
      </svg>
    ),
  },
  {
    key: "plans",
    label: "Plans",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="15" height="15">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
  },
  {
    key: "system_users",
    label: "System Users",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="15" height="15">
        <circle cx="9" cy="7" r="4" />
        <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
        <path d="M16 11l2 2 4-4" />
      </svg>
    ),
  },
];

// ══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════════

export default function Settings() {
  const [activeTab, setActiveTab] = useState("function_types");
  const { snack, showSnack, dismissSnack } = useSnackbar();

  return (
    <AdminLayout>
      <div className="settings-page">
        <div className="settings-page__header">
          <h1 className="settings-page__title">Settings</h1>
        </div>

        <div className="settings-layout">
          {/* LEFT NAV */}
          <nav className="settings-nav" aria-label="Settings sections">
            {SETTINGS_NAV.map((item) => (
              <button key={item.key} type="button"
                className={`settings-nav__item${activeTab === item.key ? " settings-nav__item--active" : ""}`}
                onClick={() => setActiveTab(item.key)}>
                <span className="settings-nav__icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </nav>

          {/* RIGHT CONTENT */}
          <div className="settings-content">
            {activeTab === "function_types" && <FunctionTypesPanel showSnack={showSnack} />}
            {activeTab === "plans"          && <PlansPanel         showSnack={showSnack} />}
            {activeTab === "system_users"   && <SystemUsersPanel   showSnack={showSnack} />}
          </div>
        </div>
      </div>

      <Snackbar snack={snack} onDismiss={dismissSnack} />
    </AdminLayout>
  );
}
